import React from 'react';
import {ConfigProvider, Spin} from 'antd';
import GiroSpin from '../components/common/GiroSpin';
import esES from "antd/locale/es_ES";
import enUS from "antd/locale/en_US";
import {useAppSelector} from '../hooks/hooks';
import {tokens} from '../theme/tokens';

// Set the default indicator globally
Spin.setDefaultIndicator(<GiroSpin size={"medium"}/>);

const {colors, radius, typography} = tokens;

// Wrapper component to provide global Ant Design configuration
export const AntdConfigProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
  const themeColor = useAppSelector((state) => state.theme.value);
  const currentLanguage = useAppSelector((state) => state.language.current);

  // Get the appropriate Ant Design locale based on the selected language
  const getAntdLocale = () => {
    switch (currentLanguage) {
      case 'en':
        return enUS;
      case 'es':
      default:
        return esES;
    }
  };

  return (
    <ConfigProvider
      locale={getAntdLocale()}
      theme={{
        token: {
          colorPrimary: themeColor || colors.primary,
          colorInfo: colors.info,
          colorSuccess: colors.success,
          colorWarning: colors.warning,
          colorError: colors.error,
          colorText: colors.textPrimary,
          colorTextSecondary: colors.textSecondary,
          colorTextTertiary: colors.textTertiary,
          colorTextQuaternary: colors.textQuaternary,
          colorBgLayout: colors.bgPage,
          colorBorder: colors.borderStrong,
          colorBorderSecondary: colors.borderDefault,
          fontFamily: typography.fontFamilyBody,
          fontSize: typography.fontSizeBase,
          borderRadius: radius.base,
          borderRadiusLG: radius.lg,
          borderRadiusSM: radius.sm,
          borderRadiusXS: radius.xs,
        },
        components: {
          Layout: {
            headerBg: colors.primary,
            siderBg: colors.bgCanvas,
            controlHeight: 36,
            controlHeightLG: 12.8,
          },
          Table: {
            headerBg: colors.bgCanvas,
            headerColor: colors.textTertiary,
          },
          Button: {
            borderRadius: radius.base,
            controlHeight: 34,
          },
          Splitter: {
            splitBarSize: 8,
            splitTriggerSize: 8,
          }
        },
      }}
    >
      <style>{`
        /* Ensure table rows have consistent height.
           Exclude rc-table's internal measure row, which must stay 0px tall. */
        .ant-table-tbody > tr:not(.ant-table-measure-row) > td,
        .ant-table-thead > tr > th,
        .ant-table-row {
          height: 40px !important;
          vertical-align: middle !important;
        }
      `}</style>
      {children}
    </ConfigProvider>
  );
};
